// Highest value the first digit of a segment can take while a second digit is still possible.
// A day of `4` cannot become two digits (max is 31), a month of `1` still can (10, 11, 12).
const SEGMENT_MAX_FIRST_DIGIT = {
  DD: 3,
  MM: 1,
}

interface AutoAdvanceResult {
  value: string;
  selection: [number, number] | null;
}

const getDigitGroups = (value: string) => Array.from(value.matchAll(/\d+/g), (match) => ({
  start: match.index ?? 0,
  end: (match.index ?? 0) + match[0].length,
  digits: match[0],
}))

export const applySegmentAutoAdvance = (value: string, caretPosition: number, format: string): AutoAdvanceResult => {
  const tokens = format.match(/[A-Za-z]+/g) || []
  const separators = format.split(/[A-Za-z]+/)

  const groups = getDigitGroups(value)
  const index = groups.findIndex(({ end }) => end === caretPosition)
  const group = groups[index]
  const token = tokens[index]
  if (!group || !token || index === tokens.length - 1) { return { value, selection: null } }

  const maxFirstDigit = SEGMENT_MAX_FIRST_DIGIT[token]
  const isComplete = group.digits.length >= token.length
  const isUnambiguous = group.digits.length === 1 && maxFirstDigit !== undefined && Number(group.digits) > maxFirstDigit
  if (!isComplete && !isUnambiguous) { return { value, selection: null } }

  const paddedValue = isComplete
    ? value
    : value.slice(0, group.start) + group.digits.padStart(token.length, '0') + value.slice(group.end)

  const nextGroup = getDigitGroups(paddedValue)[index + 1]
  if (nextGroup) { return { value: paddedValue, selection: [nextGroup.start, nextGroup.end] } }

  const separator = separators[index + 1] || ''
  const segmentEnd = group.start + Math.max(group.digits.length, token.length)
  const needsSeparator = !paddedValue.slice(segmentEnd).startsWith(separator)
  const caret = segmentEnd + separator.length

  return {
    value: needsSeparator ? paddedValue.slice(0, segmentEnd) + separator : paddedValue,
    selection: [caret, caret],
  }
}
